import { useCallback, useRef, useEffect } from "react";
import type { Tile } from "../../../shared/types";

interface Props {
  tiles: Tile[];
  sideIndex: number;
  selectedTileId?: string | null;
  onSelectTile: (tile: Tile) => void;
  onDragStartTile?: (tile: Tile) => void;
  onMoveTile?: (fromIndex: number, toIndex: number) => void;
  onMoveTileBetweenSides?: (fromSide: number, fromIndex: number, toSide: number, toIndex: number) => void;
  disabled?: boolean;
  orientation?: "top" | "bottom" | "left" | "right";
  touchDragRef?: React.MutableRefObject<{
    tile: Tile;
    sideIndex: number;
    index: number;
    ghost: HTMLElement | null;
  } | null>;
}

export default function TileRack({
  tiles,
  sideIndex,
  selectedTileId,
  onSelectTile,
  onDragStartTile,
  onMoveTile,
  onMoveTileBetweenSides,
  disabled,
  orientation = "bottom",
  touchDragRef,
}: Props) {
  const isVertical = orientation === "left" || orientation === "right";

  // Track touch drag state locally
  const dragState = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    tile: Tile | null;
    index: number;
    ghost: HTMLElement | null;
  }>({ active: false, startX: 0, startY: 0, tile: null, index: -1, ghost: null });

  // Keep latest callbacks in a ref to avoid re-attaching document listeners
  const callbacksRef = useRef({ onMoveTile, onMoveTileBetweenSides });
  callbacksRef.current = { onMoveTile, onMoveTileBetweenSides };

  const cleanupLocalDrag = useCallback(() => {
    if (dragState.current.ghost) {
      dragState.current.ghost.remove();
    }
    dragState.current = { active: false, startX: 0, startY: 0, tile: null, index: -1, ghost: null };
  }, []);

  // Attach document-level touch listeners once per mount
  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      const state = dragState.current;
      if (!state.tile || disabled) return;

      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;

      if (!state.active && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        // Start drag
        e.preventDefault();
        state.active = true;

        const ghost = document.createElement("div");
        ghost.textContent = state.tile.letter;
        ghost.className =
          "fixed z-[9999] w-10 h-12 sm:w-12 sm:h-14 rounded flex items-center justify-center font-bold text-lg sm:text-xl bg-tile text-letter shadow-2xl ring-2 ring-vsw-300 pointer-events-none select-none opacity-90";
        ghost.style.left = `${touch.clientX - 20}px`;
        ghost.style.top = `${touch.clientY - 24}px`;
        document.body.appendChild(ghost);
        state.ghost = ghost;

        if (touchDragRef) {
          touchDragRef.current = { tile: state.tile, sideIndex, index: state.index, ghost };
        }
      }

      if (state.active && state.ghost) {
        e.preventDefault();
        state.ghost.style.left = `${touch.clientX - 20}px`;
        state.ghost.style.top = `${touch.clientY - 24}px`;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const state = dragState.current;
      if (!state.tile) return; // This rack did not originate the drag

      if (!state.active) {
        // Was a tap (not a drag); let onClick fire naturally
        cleanupLocalDrag();
        return;
      }

      e.preventDefault(); // Prevent the synthetic click from firing

      const touch = e.changedTouches[0];
      if (!touch) {
        cleanupLocalDrag();
        return;
      }

      const target = document.elementFromPoint(touch.clientX, touch.clientY);

      // Check if dropped on a TileRack
      const rackEl = target?.closest("[data-tile-rack]") as HTMLElement | null;
      if (rackEl) {
        const toSide = parseInt(rackEl.getAttribute("data-tile-rack")!);
        const tileEl = target?.closest("[data-tile-index]") as HTMLElement | null;
        const toIndex = tileEl
          ? parseInt(tileEl.getAttribute("data-tile-index")!)
          : rackEl.querySelectorAll("[data-tile-index]").length;

        if (toSide === sideIndex) {
          callbacksRef.current.onMoveTile?.(state.index, toIndex);
        } else {
          callbacksRef.current.onMoveTileBetweenSides?.(
            sideIndex,
            state.index,
            toSide,
            toIndex
          );
        }

        // The drop was handled; clear shared ref
        if (touchDragRef?.current?.sideIndex === sideIndex) {
          touchDragRef.current = null;
        }
      }

      cleanupLocalDrag();
    };

    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      cleanupLocalDrag();
    };
  }, [sideIndex, disabled, cleanupLocalDrag, touchDragRef]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, tile: Tile, index: number) => {
      if (disabled) return;
      const touch = e.touches[0];
      dragState.current.startX = touch.clientX;
      dragState.current.startY = touch.clientY;
      dragState.current.tile = tile;
      dragState.current.index = index;
      dragState.current.active = false;
    },
    [disabled]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      const tile = tiles[index];
      e.dataTransfer.setData(
        "application/json",
        JSON.stringify({ sideIndex, tileIndex: index, tile })
      );
      e.dataTransfer.effectAllowed = "move";
      if (onDragStartTile) {
        onDragStartTile(tile);
      }
    },
    [disabled, tiles, sideIndex, onDragStartTile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      const data = e.dataTransfer.getData("application/json");
      if (!data) return;
      try {
        const parsed = JSON.parse(data) as {
          sideIndex: number;
          tileIndex: number;
          tile: Tile;
        };
        if (parsed.sideIndex === sideIndex) {
          if (onMoveTile) {
            onMoveTile(parsed.tileIndex, index);
          }
        } else {
          if (onMoveTileBetweenSides) {
            onMoveTileBetweenSides(
              parsed.sideIndex,
              parsed.tileIndex,
              sideIndex,
              index
            );
          }
        }
      } catch {
        // ignore invalid drop data
      }
    },
    [sideIndex, onMoveTile, onMoveTileBetweenSides]
  );

  return (
    <div
      data-tile-rack={sideIndex}
      onDragOver={handleDragOver}
      className={`flex gap-1.5 sm:gap-2 p-2 sm:p-3 bg-board-cell rounded-lg overflow-auto max-w-full
        ${isVertical ? "flex-col items-center max-h-full" : "flex-row flex-wrap justify-center"}
      `}
    >
      {tiles.map((tile, index) => {
        const isSelected = selectedTileId === tile.id;
        return (
          <button
            key={tile.id}
            data-tile-index={index}
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onTouchStart={(e) => handleTouchStart(e, tile, index)}
            onClick={() => !disabled && onSelectTile(tile)}
            disabled={disabled}
            className={`relative w-10 h-12 sm:w-12 sm:h-14 rounded flex items-center justify-center font-bold select-none transition-all flex-shrink-0
              ${isSelected
                ? "bg-vsw-400 text-white scale-110 shadow-lg ring-2 ring-vsw-300"
                : "bg-tile text-letter shadow hover:scale-105"
              }
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}
            `}
          >
            <span className="text-lg sm:text-xl font-bold">{tile.letter}</span>
            <span className="absolute bottom-0.5 right-1 text-[9px] sm:text-[10px] font-mono text-letter/70">
              {tile.value}
            </span>
          </button>
        );
      })}
      {tiles.length === 0 && (
        <div className="text-vsw-300 text-sm italic">Rack empty</div>
      )}
    </div>
  );
}
